import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal,
  TextInput, Alert, Dimensions, FlatList, RefreshControl, Animated,
  Platform, StatusBar, BackHandler, KeyboardAvoidingView, Share, Switch,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

const API_BASE = 'https://api.jobeng.ai';
const CACHE_TTL = 15 * 60 * 1000;

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6',
};

// ─── MODULE DEFINITIONS ────────────────────────────────────────────────────────
const MODULE_TABS = {
  crm:         [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'deals', icon: 'briefcase', label: 'Deals' }, { id: 'contacts', icon: 'account-group', label: 'Contacts' }, { id: 'pipeline', icon: 'trending-up', label: 'Pipeline' }, { id: 'activities', icon: 'calendar', label: 'Activities' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  finance:     [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'transactions', icon: 'swap-horizontal', label: 'Transactions' }, { id: 'invoices', icon: 'file-document', label: 'Invoices' }, { id: 'budgets', icon: 'chart-pie', label: 'Budgets' }, { id: 'accounts', icon: 'bank', label: 'Accounts' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  hr:          [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'employees', icon: 'account-group', label: 'Employees' }, { id: 'payroll', icon: 'currency-gbp', label: 'Payroll' }, { id: 'leave', icon: 'beach', label: 'Leave' }, { id: 'performance', icon: 'star', label: 'Performance' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  inventory:   [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'products', icon: 'package', label: 'Products' }, { id: 'warehouses', icon: 'warehouse', label: 'Warehouses' }, { id: 'transfers', icon: 'truck', label: 'Transfers' }, { id: 'alerts', icon: 'alert', label: 'Alerts' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  analytics:   [{ id: 'dashboard', icon: 'view-dashboard', label: 'Overview' }, { id: 'kpis', icon: 'flag', label: 'KPIs' }, { id: 'revenue', icon: 'cash', label: 'Revenue' }, { id: 'customers', icon: 'account-group', label: 'Customers' }, { id: 'forecast', icon: 'crystal-ball', label: 'Forecast' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  projects:    [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'projects', icon: 'folder', label: 'Projects' }, { id: 'tasks', icon: 'check-circle', label: 'Tasks' }, { id: 'resources', icon: 'account-multiple', label: 'Resources' }, { id: 'timeline', icon: 'timeline', label: 'Timeline' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  payroll:     [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'payruns', icon: 'bank-transfer', label: 'Pay Runs' }, { id: 'employees', icon: 'account-group', label: 'Employees' }, { id: 'paye', icon: 'bank-outline', label: 'PAYE' }, { id: 'pension', icon: 'shield-account', label: 'Pension' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  procurement: [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'orders', icon: 'file-document', label: 'PO Orders' }, { id: 'suppliers', icon: 'domain', label: 'Suppliers' }, { id: 'contracts', icon: 'file-sign', label: 'Contracts' }, { id: 'budgets', icon: 'chart-pie', label: 'Budgets' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
  production:  [{ id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' }, { id: 'workorders', icon: 'clipboard-list', label: 'Work Orders' }, { id: 'bom', icon: 'sitemap', label: 'BOM' }, { id: 'quality', icon: 'check-decagram', label: 'QC' }, { id: 'schedule', icon: 'calendar', label: 'Schedule' }, { id: 'reports', icon: 'chart-bar', label: 'Reports' }],
};

// ─── RICH MOCK DATA PER MODULE ─────────────────────────────────────────────────
const buildModuleData = (moduleId) => {
  const base = {
    crm: {
      kpis: [{ l: 'Total Customers', v: '1,847', t: '+47', c: BRAND.success, i: 'account-group' }, { l: 'Open Deals', v: '156', t: '+12', c: BRAND.blue, i: 'briefcase' }, { l: 'Revenue MTD', v: '£384k', t: '+18%', c: BRAND.success, i: 'cash' }, { l: 'Conversion Rate', v: '24.8%', t: '+3%', c: BRAND.mint, i: 'trending-up' }],
      records: [
        { id: 'C001', title: 'Acme Corporation', sub: 'John Smith · Enterprise', value: '£125,000', status: 'active', badge: 'Won', badgeColor: BRAND.success, icon: 'domain', meta: 'Closed 3 Jan' },
        { id: 'C002', title: 'Beta Industries', sub: 'Jane Doe · Mid-Market', value: '£89,000', status: 'prospect', badge: 'Proposal', badgeColor: BRAND.info, icon: 'briefcase', meta: 'Due 15 Feb' },
        { id: 'C003', title: 'Gamma Solutions', sub: 'Bob Wilson · SMB', value: '£156,000', status: 'active', badge: 'Negotiation', badgeColor: BRAND.warning, icon: 'handshake', meta: 'Updated today' },
        { id: 'C004', title: 'Delta Tech Ltd', sub: 'Alice Brown · Enterprise', value: '£230,000', status: 'lead', badge: 'Qualified', badgeColor: BRAND.purple, icon: 'account', meta: 'New lead' },
        { id: 'C005', title: 'Nexus Healthcare', sub: 'Dr. Sara Patel · Healthcare', value: '£75,000', status: 'lead', badge: 'Lead', badgeColor: BRAND.canary, icon: 'hospital', meta: '2 days ago' },
        { id: 'C006', title: 'TechCorp Inc', sub: 'Mike Chang · SaaS', value: '£45,000', status: 'won', badge: 'Won', badgeColor: BRAND.success, icon: 'laptop', meta: 'Won yesterday' },
      ],
      pipeline: [
        { stage: 'Lead', count: 28, value: '£280k', color: BRAND.canary, pct: 30 },
        { stage: 'Qualified', count: 22, value: '£418k', color: BRAND.info, pct: 52 },
        { stage: 'Proposal', count: 15, value: '£375k', color: BRAND.purple, pct: 67 },
        { stage: 'Negotiation', count: 9, value: '£315k', color: BRAND.warning, pct: 80 },
        { stage: 'Won', count: 23, value: '£575k', color: BRAND.success, pct: 100 },
      ],
    },
    finance: {
      kpis: [{ l: 'Revenue MTD', v: '£2.45M', t: '+18%', c: BRAND.success, i: 'trending-up' }, { l: 'Expenses MTD', v: '£1.89M', t: '-3%', c: BRAND.warning, i: 'trending-down' }, { l: 'Net Profit', v: '£560k', t: '+22%', c: BRAND.success, i: 'cash' }, { l: 'Cash Flow', v: '£450k', t: '+11%', c: BRAND.mint, i: 'bank' }],
      records: [
        { id: 'T001', title: 'Invoice Payment – Acme Corp', sub: 'INV-2024-089 · Received', value: '+£22,000', status: 'completed', badge: 'Income', badgeColor: BRAND.success, icon: 'cash-plus', meta: '2h ago' },
        { id: 'T002', title: 'Office Rent – January', sub: 'Ref: RENT-0124', value: '-£8,500', status: 'completed', badge: 'Expense', badgeColor: BRAND.danger, icon: 'home', meta: 'Yesterday' },
        { id: 'T003', title: 'Software Licenses', sub: 'Annual renewal – SaaS tools', value: '-£4,200', status: 'completed', badge: 'Expense', badgeColor: BRAND.danger, icon: 'laptop', meta: '3 days ago' },
        { id: 'T004', title: 'Invoice Payment – Beta Ltd', sub: 'INV-2024-088 · Received', value: '+£15,000', status: 'completed', badge: 'Income', badgeColor: BRAND.success, icon: 'cash-plus', meta: '3 days ago' },
        { id: 'T005', title: 'Invoice Overdue – XYZ Corp', sub: 'INV-2024-012 · 5 days late', value: '£8,500', status: 'overdue', badge: 'Overdue', badgeColor: BRAND.danger, icon: 'clock-alert', meta: 'Action required' },
        { id: 'T006', title: 'Payroll – December', sub: '245 employees processed', value: '-£920,000', status: 'completed', badge: 'Expense', badgeColor: BRAND.danger, icon: 'bank-transfer', meta: '1 Jan' },
      ],
      accounts: [
        { name: 'Cash & Bank', balance: '£450,000', type: 'asset', color: BRAND.success },
        { name: 'Accounts Receivable', balance: '£125,000', type: 'asset', color: BRAND.blue },
        { name: 'Accounts Payable', balance: '£85,000', type: 'liability', color: BRAND.danger },
        { name: 'Fixed Assets', balance: '£2.1M', type: 'asset', color: BRAND.purple },
      ],
    },
    hr: {
      kpis: [{ l: 'Total Employees', v: '245', t: '+12', c: BRAND.success, i: 'account-group' }, { l: 'New Hires (MTD)', v: '12', t: 'this month', c: BRAND.blue, i: 'account-plus' }, { l: 'Turnover Rate', v: '8.2%', t: '-1.2%', c: BRAND.success, i: 'account-remove' }, { l: 'Avg Salary', v: '£45k', t: '+3%', c: BRAND.purple, i: 'currency-gbp' }],
      records: [
        { id: 'E001', title: 'Sarah Johnson', sub: 'Senior Developer · Engineering', value: '£72,000', status: 'active', badge: 'Active', badgeColor: BRAND.success, icon: 'account', meta: 'Hired 15 Jan' },
        { id: 'E002', title: 'Mike Chen', sub: 'Marketing Manager · Marketing', value: '£58,000', status: 'active', badge: 'Active', badgeColor: BRAND.success, icon: 'account', meta: 'Hired 10 Jan' },
        { id: 'E003', title: 'Emma Wilson', sub: 'UX Designer · Design', value: '£52,000', status: 'active', badge: 'Active', badgeColor: BRAND.success, icon: 'account', meta: 'Hired 8 Jan' },
        { id: 'E004', title: 'James Taylor', sub: 'Data Analyst · Analytics', value: '£65,000', status: 'active', badge: 'Active', badgeColor: BRAND.success, icon: 'account', meta: 'Hired 5 Jan' },
        { id: 'E005', title: 'Lucy Brown', sub: 'Sales Executive · Sales', value: '£44,000', status: 'leave', badge: 'On Leave', badgeColor: BRAND.warning, icon: 'account', meta: 'Returns 20 Feb' },
        { id: 'E006', title: 'Robert Davis', sub: 'Ops Manager · Operations', value: '£68,000', status: 'active', badge: 'Active', badgeColor: BRAND.success, icon: 'account', meta: 'Since 2022' },
      ],
      departments: [
        { name: 'Engineering', count: 85, budget: '£3.8M', lead: 'Tom Harris', color: BRAND.blue },
        { name: 'Sales', count: 45, budget: '£2.1M', lead: 'Jane Doe', color: '#f97316' },
        { name: 'Marketing', count: 25, budget: '£1.2M', lead: 'Mike Chen', color: BRAND.purple },
        { name: 'Operations', count: 40, budget: '£1.8M', lead: 'Robert Davis', color: BRAND.mint },
        { name: 'Finance', count: 20, budget: '£0.9M', lead: 'Chris Lee', color: BRAND.success },
        { name: 'HR & Admin', count: 30, budget: '£1.1M', lead: 'Amy Clark', color: BRAND.warning },
      ],
    },
    inventory: {
      kpis: [{ l: 'Total SKUs', v: '1,247', t: '+34', c: BRAND.success, i: 'package' }, { l: 'Stock Value', v: '£890k', t: '+7%', c: BRAND.blue, i: 'cash' }, { l: 'Low Stock Items', v: '23', t: 'Reorder!', c: BRAND.danger, i: 'alert' }, { l: 'Turnover Rate', v: '4.2x', t: '+0.3x', c: BRAND.success, i: 'refresh' }],
      records: [
        { id: 'P001', title: 'Laptop Pro 15"', sub: 'SKU: LP-15-001 · Electronics', value: '£67,500', status: 'in-stock', badge: 'In Stock', badgeColor: BRAND.success, icon: 'laptop', meta: 'Qty: 45' },
        { id: 'P002', title: 'Wireless Mouse', sub: 'SKU: WM-001 · Peripherals', value: '£11,700', status: 'in-stock', badge: 'In Stock', badgeColor: BRAND.success, icon: 'mouse', meta: 'Qty: 234' },
        { id: 'P003', title: 'USB-C Cable 2m', sub: 'SKU: UC-001 · Accessories', value: '£360', status: 'low-stock', badge: 'Low Stock', badgeColor: BRAND.danger, icon: 'cable-data', meta: 'Qty: 12 ⚠' },
        { id: 'P004', title: 'Office Chair Pro', sub: 'SKU: OC-100 · Furniture', value: '£24,000', status: 'in-stock', badge: 'In Stock', badgeColor: BRAND.success, icon: 'seat', meta: 'Qty: 60' },
        { id: 'P005', title: '4K Monitor 27"', sub: 'SKU: MN-270 · Electronics', value: '£54,000', status: 'in-stock', badge: 'In Stock', badgeColor: BRAND.success, icon: 'monitor', meta: 'Qty: 36' },
        { id: 'P006', title: 'Printer Ink Black', sub: 'SKU: PI-BK1 · Consumables', value: '£480', status: 'low-stock', badge: 'Low Stock', badgeColor: BRAND.danger, icon: 'printer', meta: 'Qty: 8 ⚠' },
      ],
      warehouses: [
        { name: 'Main Warehouse', location: 'London', capacity: 85, items: 847, color: BRAND.blue },
        { name: 'North Depot', location: 'Manchester', capacity: 62, items: 245, color: BRAND.mint },
        { name: 'South Storage', location: 'Birmingham', capacity: 78, items: 155, color: BRAND.purple },
      ],
    },
    analytics: {
      kpis: [{ l: 'Active Reports', v: '18', t: '+4', c: BRAND.blue, i: 'file-chart' }, { l: 'Dashboards', v: '7', t: '+2', c: BRAND.purple, i: 'view-dashboard' }, { l: 'KPIs Tracked', v: '42', t: '+8', c: BRAND.success, i: 'flag' }, { l: 'Data Points', v: '2.4M', t: '+12%', c: BRAND.mint, i: 'database' }],
      records: [
        { id: 'R001', title: 'Q4 Revenue Report', sub: 'Finance · Full year analysis', value: 'PDF 2.4MB', status: 'ready', badge: 'Ready', badgeColor: BRAND.success, icon: 'file-chart', meta: 'Generated today' },
        { id: 'R002', title: 'Customer Segmentation', sub: 'CRM · 1,847 contacts analysed', value: 'Excel 1.1MB', status: 'ready', badge: 'Ready', badgeColor: BRAND.success, icon: 'account-group', meta: '2 days ago' },
        { id: 'R003', title: 'HR Performance Review', sub: 'HR · 245 employees scored', value: 'PDF 0.8MB', status: 'ready', badge: 'Ready', badgeColor: BRAND.success, icon: 'star', meta: '1 week ago' },
        { id: 'R004', title: 'Inventory Optimisation', sub: 'Inventory · Reorder analysis', value: 'Excel 0.6MB', status: 'processing', badge: 'Processing', badgeColor: BRAND.warning, icon: 'warehouse', meta: 'Running now' },
        { id: 'R005', title: 'Sales Forecast Q1 2025', sub: 'CRM + Finance · AI-powered', value: 'PDF 1.8MB', status: 'ready', badge: 'New', badgeColor: BRAND.blue, icon: 'crystal-ball', meta: '30 min ago' },
      ],
      kpiList: [
        { name: 'Revenue Growth', value: '+18.2%', target: '+15%', status: 'above', color: BRAND.success },
        { name: 'Profit Margin', value: '22.9%', target: '20%', status: 'above', color: BRAND.success },
        { name: 'Customer CSAT', value: '92%', target: '90%', status: 'above', color: BRAND.success },
        { name: 'Employee NPS', value: '78', target: '80', status: 'below', color: BRAND.warning },
        { name: 'On-Time Delivery', value: '87%', target: '90%', status: 'below', color: BRAND.warning },
        { name: 'Inventory Turnover', value: '4.2x', target: '4.0x', status: 'above', color: BRAND.success },
        { name: 'Cash Conversion', value: '38 days', target: '45 days', status: 'above', color: BRAND.success },
      ],
    },
    projects: {
      kpis: [{ l: 'Active Projects', v: '34', t: '+6', c: BRAND.success, i: 'folder' }, { l: 'Tasks This Week', v: '87', t: '-12 done', c: BRAND.blue, i: 'check-circle' }, { l: 'On-Time Rate', v: '87%', t: '+2%', c: BRAND.success, i: 'clock-check' }, { l: 'Budget Utilisation', v: '68%', t: '-5%', c: BRAND.warning, i: 'cash-multiple' }],
      records: [
        { id: 'PR001', title: 'Alpha Initiative', sub: 'Sarah Johnson · Engineering', value: '£125,000', status: 'active', badge: '75%', badgeColor: BRAND.success, icon: 'folder', meta: 'Due 15 Mar' },
        { id: 'PR002', title: 'Beta Platform', sub: 'Mike Chen · Marketing', value: '£89,000', status: 'active', badge: '45%', badgeColor: BRAND.warning, icon: 'folder', meta: 'Due 28 Mar' },
        { id: 'PR003', title: 'Gamma Integration', sub: 'Emma Wilson · Design', value: '£67,000', status: 'active', badge: '90%', badgeColor: BRAND.success, icon: 'folder', meta: 'Due 5 Feb' },
        { id: 'PR004', title: 'Delta CRM Upgrade', sub: 'James Taylor · Tech', value: '£45,000', status: 'planning', badge: '10%', badgeColor: BRAND.info, icon: 'folder', meta: 'Starts 1 Feb' },
        { id: 'PR005', title: 'Mobile App v3', sub: 'Full team · Product', value: '£200,000', status: 'active', badge: '60%', badgeColor: BRAND.blue, icon: 'folder', meta: 'Due 30 Apr' },
        { id: 'PR006', title: 'ERP Migration', sub: 'Robert Davis · Ops', value: '£350,000', status: 'active', badge: '30%', badgeColor: BRAND.warning, icon: 'folder', meta: 'Due 31 May' },
      ],
      tasks: [
        { title: 'Database Migration', project: 'Alpha Initiative', assignee: 'John Doe', due: '1 Feb', priority: 'high', status: 'in-progress' },
        { title: 'UI Design Review', project: 'Beta Platform', assignee: 'Jane Smith', due: '30 Jan', priority: 'medium', status: 'pending' },
        { title: 'API Testing', project: 'Gamma Integration', assignee: 'Bob Wilson', due: '28 Jan', priority: 'high', status: 'completed' },
        { title: 'Stakeholder Presentation', project: 'Delta CRM', assignee: 'Sarah Johnson', due: '5 Feb', priority: 'high', status: 'pending' },
        { title: 'Sprint Planning', project: 'Mobile App v3', assignee: 'Mike Chen', due: '3 Feb', priority: 'medium', status: 'in-progress' },
      ],
    },
    payroll: {
      kpis: [{ l: 'Monthly Payroll', v: '£920k', t: '+3%', c: BRAND.success, i: 'bank-transfer' }, { l: 'Employees Paid', v: '245', t: 'all paid', c: BRAND.blue, i: 'account-check' }, { l: 'PAYE Due', v: '£186k', t: '8 days', c: BRAND.danger, i: 'bank-outline' }, { l: 'Pension', v: '£92k', t: 'this month', c: BRAND.purple, i: 'shield-account' }],
      records: [
        { id: 'PAY001', title: 'December 2024 Payroll', sub: '245 employees · All depts', value: '£920,000', status: 'completed', badge: 'Paid', badgeColor: BRAND.success, icon: 'bank-transfer', meta: '31 Dec 2024' },
        { id: 'PAY002', title: 'November 2024 Payroll', sub: '243 employees · All depts', value: '£912,000', status: 'completed', badge: 'Paid', badgeColor: BRAND.success, icon: 'bank-transfer', meta: '30 Nov 2024' },
        { id: 'PAY003', title: 'January 2025 Payroll', sub: '245 employees · Pending approval', value: '£924,000', status: 'pending', badge: 'Pending', badgeColor: BRAND.warning, icon: 'bank-transfer', meta: 'Due 31 Jan' },
        { id: 'PAY004', title: 'PAYE – Q3 2024', sub: 'HMRC submission', value: '£558,000', status: 'completed', badge: 'Submitted', badgeColor: BRAND.success, icon: 'bank-outline', meta: '19 Oct 2024' },
        { id: 'PAY005', title: 'PAYE – Q4 2024', sub: 'HMRC submission', value: '£571,000', status: 'due', badge: 'Due Soon', badgeColor: BRAND.danger, icon: 'bank-outline', meta: 'Due 19 Jan' },
      ],
    },
    procurement: {
      kpis: [{ l: 'Open POs', v: '43', t: '+7', c: BRAND.warning, i: 'file-document' }, { l: 'Suppliers', v: '128', t: '+12', c: BRAND.blue, i: 'domain' }, { l: 'Spend MTD', v: '£340k', t: '+9%', c: BRAND.success, i: 'cash' }, { l: 'Savings', v: '£28k', t: 'this month', c: BRAND.mint, i: 'piggy-bank' }],
      records: [
        { id: 'PO001', title: 'Server Hardware Upgrade', sub: 'Dell Technologies · IT Dept', value: '£34,000', status: 'approved', badge: 'Approved', badgeColor: BRAND.success, icon: 'server', meta: 'Delivery 5 Feb' },
        { id: 'PO002', title: 'Office Supplies Q1', sub: 'Staples Corp · Admin', value: '£2,800', status: 'pending', badge: 'Pending', badgeColor: BRAND.warning, icon: 'pencil-box', meta: 'Awaiting approval' },
        { id: 'PO003', title: 'Marketing Print Run', sub: 'PrintFast Ltd · Marketing', value: '£8,500', status: 'delivered', badge: 'Delivered', badgeColor: BRAND.success, icon: 'printer', meta: 'Received 20 Jan' },
        { id: 'PO004', title: 'Warehouse Shelving', sub: 'Racking Systems Ltd · Ops', value: '£15,000', status: 'approved', badge: 'Approved', badgeColor: BRAND.success, icon: 'warehouse', meta: 'Delivery 12 Feb' },
        { id: 'PO005', title: 'Software Licences', sub: 'Microsoft · IT Dept', value: '£12,400', status: 'completed', badge: 'Completed', badgeColor: BRAND.success, icon: 'laptop', meta: 'Received 15 Jan' },
      ],
    },
    production: {
      kpis: [{ l: 'Work Orders', v: '28', t: '+5 new', c: BRAND.blue, i: 'clipboard-list' }, { l: 'Output Today', v: '1,240', t: '+6%', c: BRAND.success, i: 'factory' }, { l: 'OEE', v: '78%', t: '+3%', c: BRAND.success, i: 'gauge' }, { l: 'Defect Rate', v: '0.8%', t: '-0.2%', c: BRAND.success, i: 'alert-circle' }],
      records: [
        { id: 'WO001', title: 'Batch A – Component X', sub: 'Line 1 · Target: 500 units', value: '500 units', status: 'completed', badge: 'Done', badgeColor: BRAND.success, icon: 'factory', meta: 'Completed 9am' },
        { id: 'WO002', title: 'Batch B – Assembly Y', sub: 'Line 2 · Target: 300 units', value: '280 units', status: 'in-progress', badge: '93%', badgeColor: BRAND.blue, icon: 'cog', meta: 'Est. 2h remaining' },
        { id: 'WO003', title: 'Quality Check – Lot C', sub: 'QC Team · 200 units inspection', value: '200 units', status: 'pending', badge: 'QC Pending', badgeColor: BRAND.warning, icon: 'check-decagram', meta: 'Starts 3pm' },
        { id: 'WO004', title: 'Maintenance – Line 3', sub: 'Engineering · Planned downtime', value: '4 hours', status: 'scheduled', badge: 'Scheduled', badgeColor: BRAND.info, icon: 'wrench', meta: 'Tomorrow 6am' },
        { id: 'WO005', title: 'Batch D – Final Product Z', sub: 'Line 1 · Target: 800 units', value: '800 units', status: 'planned', badge: 'Planned', badgeColor: BRAND.purple, icon: 'package', meta: 'Starts tomorrow' },
      ],
    },
  };
  return base[moduleId] || base.crm;
};

// ─── STATUS CONFIG ─────────────────────────────────────────────────────────────
const statusColor = (s) => {
  const m = { active: BRAND.success, completed: BRAND.success, won: BRAND.success, delivered: BRAND.success, paid: BRAND.success, ready: BRAND.success, 'in-progress': BRAND.info, pending: BRAND.warning, scheduled: BRAND.info, planning: BRAND.info, processing: BRAND.warning, 'low-stock': BRAND.danger, overdue: BRAND.danger, due: BRAND.danger, lost: BRAND.danger, inactive: BRAND.sub };
  return m[s] || BRAND.sub;
};

// ─── FORM CONFIG PER MODULE ────────────────────────────────────────────────────
const FORM_FIELDS = {
  crm:         [{ key: 'company', label: 'Company Name', icon: 'domain', required: true }, { key: 'contact', label: 'Contact Name', icon: 'account', required: true }, { key: 'email', label: 'Email', icon: 'email', kb: 'email-address' }, { key: 'phone', label: 'Phone', icon: 'phone', kb: 'phone-pad' }, { key: 'value', label: 'Deal Value (£)', icon: 'cash', kb: 'decimal-pad' }, { key: 'notes', label: 'Notes', icon: 'note-text', multi: true }],
  finance:     [{ key: 'type', label: 'Type (income/expense)', icon: 'swap-horizontal', required: true }, { key: 'category', label: 'Category', icon: 'tag', required: true }, { key: 'description', label: 'Description', icon: 'text', required: true }, { key: 'amount', label: 'Amount (£)', icon: 'cash', kb: 'decimal-pad', required: true }, { key: 'reference', label: 'Reference', icon: 'pound' }, { key: 'notes', label: 'Notes', icon: 'note-text', multi: true }],
  hr:          [{ key: 'name', label: 'Full Name', icon: 'account', required: true }, { key: 'email', label: 'Email', icon: 'email', kb: 'email-address', required: true }, { key: 'position', label: 'Job Title', icon: 'briefcase', required: true }, { key: 'department', label: 'Department', icon: 'domain' }, { key: 'salary', label: 'Salary (£)', icon: 'cash', kb: 'decimal-pad' }, { key: 'startDate', label: 'Start Date', icon: 'calendar' }],
  inventory:   [{ key: 'name', label: 'Product Name', icon: 'package', required: true }, { key: 'sku', label: 'SKU', icon: 'barcode', required: true }, { key: 'category', label: 'Category', icon: 'tag' }, { key: 'quantity', label: 'Quantity', icon: 'counter', kb: 'number-pad', required: true }, { key: 'price', label: 'Price (£)', icon: 'cash', kb: 'decimal-pad' }, { key: 'location', label: 'Warehouse Location', icon: 'warehouse' }],
  projects:    [{ key: 'name', label: 'Project Name', icon: 'folder', required: true }, { key: 'manager', label: 'Project Manager', icon: 'account', required: true }, { key: 'budget', label: 'Budget (£)', icon: 'cash', kb: 'decimal-pad' }, { key: 'startDate', label: 'Start Date', icon: 'calendar' }, { key: 'endDate', label: 'End Date', icon: 'calendar-end' }, { key: 'description', label: 'Description', icon: 'text', multi: true }],
  payroll:     [{ key: 'employee', label: 'Employee Name', icon: 'account', required: true }, { key: 'period', label: 'Pay Period', icon: 'calendar', required: true }, { key: 'gross', label: 'Gross Pay (£)', icon: 'cash', kb: 'decimal-pad', required: true }, { key: 'tax', label: 'Tax (£)', icon: 'percent', kb: 'decimal-pad' }, { key: 'net', label: 'Net Pay (£)', icon: 'cash-check', kb: 'decimal-pad' }, { key: 'notes', label: 'Notes', icon: 'note-text', multi: true }],
  procurement: [{ key: 'supplier', label: 'Supplier Name', icon: 'domain', required: true }, { key: 'item', label: 'Item / Service', icon: 'package', required: true }, { key: 'quantity', label: 'Quantity', icon: 'counter', kb: 'number-pad' }, { key: 'value', label: 'Total Value (£)', icon: 'cash', kb: 'decimal-pad', required: true }, { key: 'delivery', label: 'Delivery Date', icon: 'truck' }, { key: 'notes', label: 'Notes', icon: 'note-text', multi: true }],
  production:  [{ key: 'workOrder', label: 'Work Order Title', icon: 'clipboard-list', required: true }, { key: 'product', label: 'Product/Batch', icon: 'package', required: true }, { key: 'quantity', label: 'Target Quantity', icon: 'counter', kb: 'number-pad', required: true }, { key: 'line', label: 'Production Line', icon: 'factory' }, { key: 'startDate', label: 'Start Date', icon: 'calendar' }, { key: 'notes', label: 'Notes', icon: 'note-text', multi: true }],
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
const ERPModuleScreen = ({ navigation, route }) => {
  const { module } = route?.params || {};
  const moduleId = module?.id || 'crm';
  const T = BRAND;
  const moduleColor = module?.color || T.blue;
  const moduleData = useMemo(() => buildModuleData(moduleId), [moduleId]);
  const tabs = MODULE_TABS[moduleId] || MODULE_TABS.crm;
  const formFields = FORM_FIELDS[moduleId] || FORM_FIELDS.crm;

  const [activeTab, setActiveTab] = useState('dashboard');
  const [records, setRecords] = useState(moduleData.records || []);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modals
  const [showAdd, setShowAdd] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showAI, setShowAI] = useState(false);

  // Form
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // AI
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState([{ role: 'assistant', text: `I can help you with ${module?.name || 'this module'}. Ask me anything about your data, trends, or recommendations.`, ts: new Date() }]);
  const [aiLoading, setAiLoading] = useState(false);

  // Animations
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }),
      Animated.spring(slideAnim, { toValue: 0, friction: 8, useNativeDriver: true }),
    ]).start();
    loadCached();
  }, [moduleId]);

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showAdd || showDetail || showReport || showFilter || showAI) {
        setShowAdd(false); setShowDetail(false); setShowReport(false); setShowFilter(false); setShowAI(false);
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [showAdd, showDetail, showReport, showFilter, showAI]);

  const loadCached = async () => {
    try {
      const cached = await AsyncStorage.getItem(`erp_module_${moduleId}_v2`);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL && data.records) setRecords(data.records);
      }
    } catch {}
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setRecords(buildModuleData(moduleId).records || []);
    await AsyncStorage.setItem(`erp_module_${moduleId}_v2`, JSON.stringify({ ts: Date.now(), data: { records } }));
    setRefreshing(false);
  };

  const filteredRecords = useMemo(() => {
    let r = [...records];
    if (searchQuery) r = r.filter(x => (x.title + x.sub).toLowerCase().includes(searchQuery.toLowerCase()));
    if (filterStatus !== 'all') r = r.filter(x => x.status === filterStatus);
    return r;
  }, [records, searchQuery, filterStatus]);

  const handleSave = async () => {
    setSaving(true);
    const required = formFields.filter(f => f.required && !form[f.key]);
    if (required.length > 0) {
      Alert.alert('Required Fields', `Please fill in: ${required.map(f => f.label).join(', ')}`);
      setSaving(false); return;
    }
    const newRecord = {
      id: `NEW-${Date.now()}`,
      title: form.company || form.name || form.title || form.workOrder || form.supplier || form.employee || 'New Record',
      sub: `${form.contact || form.position || form.department || form.category || ''} · New`,
      value: form.value || form.amount || form.gross || form.budget || '',
      status: 'pending',
      badge: 'New',
      badgeColor: BRAND.blue,
      icon: module?.icon || 'plus',
      meta: 'Just added',
    };
    setRecords(prev => [newRecord, ...prev]);
    await AsyncStorage.setItem(`erp_module_${moduleId}_v2`, JSON.stringify({ ts: Date.now(), data: { records: [newRecord, ...records] } }));
    setForm({});
    setSaving(false);
    setShowAdd(false);
    Alert.alert('✅ Saved', 'Record added successfully.');
  };

  const handleDelete = (record) => {
    Alert.alert('Delete Record', `Remove "${record.title}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => { setRecords(prev => prev.filter(r => r.id !== record.id)); setShowDetail(false); } },
    ]);
  };

  const handleExport = async () => {
    const lines = filteredRecords.map(r => `${r.title} | ${r.value} | ${r.status}`).join('\n');
    await Share.share({ message: `${module?.name} Export\n${'─'.repeat(30)}\n${lines}\n\nTotal: ${filteredRecords.length} records\nExported: ${new Date().toLocaleString()}`, title: `${module?.name} Export` });
  };

  const sendModuleAI = async () => {
    const q = aiInput.trim(); if (!q) return;
    setAiMessages(prev => [...prev, { role: 'user', text: q, ts: new Date() }]);
    setAiInput(''); setAiLoading(true);
    try {
      const res = await fetch('https://api.jobeng.ai/api/ai', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
        body: JSON.stringify({ model: 'claude-sonnet-4-20250514', max_tokens: 400, system: `You are an ERP module assistant for ${module?.name}. KPIs: ${JSON.stringify(moduleData.kpis)}. Records count: ${records.length}. Answer in 2-3 concise sentences with actionable advice.`, messages: [{ role: 'user', content: q }] }),
      });
      const data = await res.json();
      setAiMessages(prev => [...prev, { role: 'assistant', text: (data.text || data.content || data.choices?.[0]?.message?.content || 'I could not respond.'), ts: new Date() }]);
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', text: `Based on your ${module?.name} data: ${moduleData.kpis[0]?.l} is ${moduleData.kpis[0]?.v} (${moduleData.kpis[0]?.t}). ${records.length} total records with ${records.filter(r => r.status === 'pending').length} pending items. Focus on the items with warning status first.`, ts: new Date() }]);
    }
    setAiLoading(false);
  };

  // ── RENDERS ──────────────────────────────────────────────────────────────────

  const renderKPIs = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, paddingVertical: 14 }}>
      {moduleData.kpis?.map((kpi, i) => (
        <View key={i} style={[msStyles.kpiCard, { backgroundColor: T.surface, borderColor: T.line }]}>
          <View style={[msStyles.kpiIcon, { backgroundColor: kpi.c + '18' }]}>
            <MaterialCommunityIcons name={kpi.i} size={18} color={kpi.c} />
          </View>
          <Text style={[msStyles.kpiValue, { color: kpi.c }]}>{kpi.v}</Text>
          <Text style={[msStyles.kpiLabel, { color: T.text }]}>{kpi.l}</Text>
          <Text style={[msStyles.kpiTrend, { color: T.sub }]}>{kpi.t}</Text>
        </View>
      ))}
      <View style={{ width: 16 }} />
    </ScrollView>
  );

  const renderToolbar = () => (
    <View style={[msStyles.toolbar, { backgroundColor: T.surface, borderColor: T.line }]}>
      <View style={[msStyles.searchBox, { backgroundColor: T.bg, borderColor: T.line }]}>
        <MaterialCommunityIcons name="magnify" size={16} color={T.sub} />
        <TextInput value={searchQuery} onChangeText={setSearchQuery} placeholder="Search..." placeholderTextColor={T.sub} style={[msStyles.searchInput, { color: T.text }]} />
        {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><MaterialCommunityIcons name="close-circle" size={14} color={T.sub} /></TouchableOpacity> : null}
      </View>
      <TouchableOpacity style={[msStyles.toolBtn, { backgroundColor: T.bg, borderColor: T.line }]} onPress={() => setShowFilter(true)}>
        <MaterialCommunityIcons name="filter-variant" size={18} color={filterStatus !== 'all' ? moduleColor : T.sub} />
      </TouchableOpacity>
      <TouchableOpacity style={[msStyles.toolBtn, { backgroundColor: T.bg, borderColor: T.line }]} onPress={handleExport}>
        <MaterialCommunityIcons name="export" size={18} color={T.sub} />
      </TouchableOpacity>
      <TouchableOpacity style={[msStyles.addBtn, { backgroundColor: moduleColor }]} onPress={() => setShowAdd(true)}>
        <MaterialCommunityIcons name="plus" size={18} color="#fff" />
        <Text style={msStyles.addBtnText}>Add</Text>
      </TouchableOpacity>
    </View>
  );

  const renderRecordCard = (record) => (
    <TouchableOpacity
      key={record.id}
      style={[msStyles.recordCard, { backgroundColor: T.surface, borderColor: T.line }]}
      onPress={() => { setSelectedRecord(record); setShowDetail(true); }}
      activeOpacity={0.75}
    >
      <View style={[msStyles.recordIcon, { backgroundColor: moduleColor + '18' }]}>
        <MaterialCommunityIcons name={record.icon} size={20} color={moduleColor} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={[msStyles.recordTitle, { color: T.text }]} numberOfLines={1}>{record.title}</Text>
        <Text style={[msStyles.recordSub, { color: T.sub }]} numberOfLines={1}>{record.sub}</Text>
        <Text style={[msStyles.recordMeta, { color: T.sub }]}>{record.meta}</Text>
      </View>
      <View style={{ alignItems: 'flex-end', gap: 6 }}>
        {record.value ? <Text style={[msStyles.recordValue, { color: record.value.startsWith('+') ? T.success : record.value.startsWith('-') ? T.danger : T.text }]}>{record.value}</Text> : null}
        <View style={[msStyles.statusBadge, { backgroundColor: record.badgeColor + '20' }]}>
          <Text style={[msStyles.statusText, { color: record.badgeColor }]}>{record.badge}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderDashboard = () => (
    <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleColor} colors={[moduleColor]} />} showsVerticalScrollIndicator={false}>
      {renderKPIs()}

      {/* AI Quick Insight */}
      <TouchableOpacity style={msStyles.aiStrip} onPress={() => setShowAI(true)}>
        <LinearGradient colors={['#6366f1', '#8b5cf6']} style={msStyles.aiStripGrad}>
          <MaterialCommunityIcons name="robot-outline" size={18} color="#fff" />
          <Text style={msStyles.aiStripText}>{module?.name} AI Advisor — tap to ask questions about your data</Text>
          <MaterialCommunityIcons name="chevron-right" size={18} color="rgba(255,255,255,0.7)" />
        </LinearGradient>
      </TouchableOpacity>

      {/* Quick Actions */}
      <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
        <Text style={[msStyles.sectionTitle, { color: T.text }]}>Quick Actions</Text>
        <View style={msStyles.quickActions}>
          {[
            { l: 'Add New', i: 'plus-circle', onPress: () => setShowAdd(true) },
            { l: 'Export', i: 'export', onPress: handleExport },
            { l: 'AI Advisor', i: 'robot', onPress: () => setShowAI(true) },
            { l: 'Reports', i: 'chart-bar', onPress: () => setShowReport(true) },
          ].map((a, i) => (
            <TouchableOpacity key={i} style={[msStyles.quickAction, { backgroundColor: T.surface, borderColor: T.line }]} onPress={a.onPress}>
              <View style={[msStyles.quickActionIcon, { backgroundColor: moduleColor + '18' }]}>
                <MaterialCommunityIcons name={a.i} size={20} color={moduleColor} />
              </View>
              <Text style={[msStyles.quickActionLabel, { color: T.text }]}>{a.l}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Pipeline / Dept charts (module specific) */}
      {moduleId === 'crm' && moduleData.pipeline && (
        <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>Sales Pipeline</Text>
          <View style={[msStyles.pipelineCard, { backgroundColor: T.surface, borderColor: T.line }]}>
            {moduleData.pipeline.map((stage, i) => (
              <View key={i} style={msStyles.pipelineRow}>
                <View style={[msStyles.pipelineDot, { backgroundColor: stage.color }]} />
                <Text style={[msStyles.pipelineName, { color: T.text }]}>{stage.stage}</Text>
                <View style={msStyles.pipelineBarOuter}>
                  <View style={[msStyles.pipelineBarInner, { width: `${stage.pct}%`, backgroundColor: stage.color }]} />
                </View>
                <Text style={[msStyles.pipelineCount, { color: T.sub }]}>{stage.count}</Text>
                <Text style={[msStyles.pipelineValue, { color: stage.color }]}>{stage.value}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {moduleId === 'hr' && moduleData.departments && (
        <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>Departments</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {moduleData.departments.map((dept, i) => (
              <View key={i} style={[msStyles.deptCard, { backgroundColor: T.surface, borderColor: dept.color + '40', borderLeftColor: dept.color }]}>
                <Text style={[msStyles.deptName, { color: dept.color }]}>{dept.name}</Text>
                <Text style={[msStyles.deptCount, { color: T.text }]}>{dept.count} staff</Text>
                <Text style={[msStyles.deptBudget, { color: T.sub }]}>{dept.budget}</Text>
                <Text style={[msStyles.deptLead, { color: T.sub }]}>{dept.lead}</Text>
              </View>
            ))}
            <View style={{ width: 16 }} />
          </ScrollView>
        </View>
      )}

      {moduleId === 'finance' && moduleData.accounts && (
        <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>Account Balances</Text>
          <View style={[msStyles.accountsCard, { backgroundColor: T.surface, borderColor: T.line }]}>
            {moduleData.accounts.map((acc, i) => (
              <View key={i} style={[msStyles.accountRow, { borderBottomColor: T.line, borderBottomWidth: i < moduleData.accounts.length - 1 ? 1 : 0 }]}>
                <View style={[msStyles.accountDot, { backgroundColor: acc.color }]} />
                <Text style={[msStyles.accountName, { color: T.text }]}>{acc.name}</Text>
                <Text style={[msStyles.accountType, { color: T.sub }]}>{acc.type}</Text>
                <Text style={[msStyles.accountBalance, { color: acc.color }]}>{acc.balance}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {moduleId === 'inventory' && moduleData.warehouses && (
        <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>Warehouse Status</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {moduleData.warehouses.map((wh, i) => (
              <View key={i} style={[msStyles.whCard, { backgroundColor: T.surface, borderColor: T.line }]}>
                <MaterialCommunityIcons name="warehouse" size={24} color={wh.color} />
                <Text style={[msStyles.whName, { color: T.text }]}>{wh.name}</Text>
                <Text style={[msStyles.whLoc, { color: T.sub }]}>{wh.location}</Text>
                <View style={msStyles.whBarOuter}>
                  <View style={[msStyles.whBarInner, { width: `${wh.capacity}%`, backgroundColor: wh.capacity > 80 ? T.danger : wh.color }]} />
                </View>
                <Text style={[msStyles.whPct, { color: wh.capacity > 80 ? T.danger : T.sub }]}>{wh.capacity}% full · {wh.items} items</Text>
              </View>
            ))}
            <View style={{ width: 16 }} />
          </ScrollView>
        </View>
      )}

      {/* Analytics KPIs for analytics module */}
      {moduleId === 'analytics' && moduleData.kpiList && (
        <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>KPI Performance</Text>
          <View style={[msStyles.kpiListCard, { backgroundColor: T.surface, borderColor: T.line }]}>
            {moduleData.kpiList.map((kpi, i) => (
              <View key={i} style={[msStyles.kpiListRow, { borderBottomColor: T.line, borderBottomWidth: i < moduleData.kpiList.length - 1 ? 1 : 0 }]}>
                <View style={[msStyles.kpiStatusDot, { backgroundColor: kpi.color }]} />
                <Text style={[msStyles.kpiListName, { color: T.text }]}>{kpi.name}</Text>
                <Text style={[msStyles.kpiListTarget, { color: T.sub }]}>Target: {kpi.target}</Text>
                <Text style={[msStyles.kpiListValue, { color: kpi.color }]}>{kpi.value}</Text>
                <MaterialCommunityIcons name={kpi.status === 'above' ? 'arrow-up-bold' : 'arrow-down-bold'} size={14} color={kpi.color} />
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Projects task list */}
      {moduleId === 'projects' && moduleData.tasks && (
        <View style={{ paddingHorizontal: 16, marginBottom: 16 }}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>Active Tasks</Text>
          {moduleData.tasks.map((task, i) => (
            <View key={i} style={[msStyles.taskCard, { backgroundColor: T.surface, borderColor: T.line, borderLeftColor: task.priority === 'high' ? T.danger : task.priority === 'medium' ? T.warning : T.success }]}>
              <View style={{ flex: 1 }}>
                <Text style={[msStyles.taskTitle, { color: T.text }]}>{task.title}</Text>
                <Text style={[msStyles.taskMeta, { color: T.sub }]}>{task.project} · {task.assignee}</Text>
              </View>
              <View style={{ alignItems: 'flex-end', gap: 4 }}>
                <Text style={[msStyles.taskDue, { color: T.sub }]}>Due {task.due}</Text>
                <View style={[msStyles.statusBadge, { backgroundColor: statusColor(task.status) + '20' }]}>
                  <Text style={[msStyles.statusText, { color: statusColor(task.status) }]}>{task.status}</Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Recent Records Preview */}
      <View style={{ paddingHorizontal: 16, marginBottom: 30 }}>
        <View style={msStyles.sectionHeaderRow}>
          <Text style={[msStyles.sectionTitle, { color: T.text }]}>Recent Records</Text>
          <TouchableOpacity onPress={() => setActiveTab('data')}>
            <Text style={[msStyles.viewAll, { color: moduleColor }]}>View All</Text>
          </TouchableOpacity>
        </View>
        {records.slice(0, 4).map(r => renderRecordCard(r))}
      </View>
    </ScrollView>
  );

  const renderDataTab = () => (
    <View style={{ flex: 1 }}>
      {renderToolbar()}
      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={moduleColor} />} style={{ flex: 1, padding: 16 }}>
        <Text style={[msStyles.sectionTitle, { color: T.text }]}>{filteredRecords.length} Records</Text>
        {filteredRecords.map(r => renderRecordCard(r))}
        {filteredRecords.length === 0 && (
          <View style={msStyles.emptyState}>
            <MaterialCommunityIcons name="database-off" size={52} color={T.line} />
            <Text style={[msStyles.emptyTitle, { color: T.text }]}>No records found</Text>
            <Text style={[msStyles.emptySub, { color: T.sub }]}>{searchQuery ? 'Try a different search' : 'Add your first record to get started'}</Text>
            <TouchableOpacity style={[msStyles.emptyBtn, { backgroundColor: moduleColor }]} onPress={() => setShowAdd(true)}>
              <MaterialCommunityIcons name="plus" size={16} color="#fff" />
              <Text style={msStyles.emptyBtnText}>Add First Record</Text>
            </TouchableOpacity>
          </View>
        )}
        <View style={{ height: 80 }} />
      </ScrollView>
    </View>
  );

  const renderReportsTab = () => (
    <ScrollView style={{ flex: 1, padding: 16 }}>
      <Text style={[msStyles.sectionTitle, { color: T.text }]}>Available Reports</Text>
      {[
        { title: `${module?.name} Summary`, sub: 'Full overview of all records', icon: 'file-chart', type: 'summary' },
        { title: 'Performance Report', sub: 'KPI analysis & trends', icon: 'trending-up', type: 'performance' },
        { title: 'Monthly Report', sub: `${new Date().toLocaleString('default', { month: 'long' })} detailed breakdown`, icon: 'calendar-month', type: 'monthly' },
        { title: 'Comparison Report', sub: 'Period-over-period analysis', icon: 'chart-bar', type: 'comparison' },
        { title: 'Audit Trail', sub: 'Full activity log', icon: 'clipboard-text-clock', type: 'audit' },
        { title: 'AI-Generated Report', sub: 'Claude-powered business intelligence', icon: 'robot', type: 'ai' },
      ].map((rep, i) => (
        <TouchableOpacity key={i} style={[msStyles.reportRow, { backgroundColor: T.surface, borderColor: T.line }]} onPress={() => setShowReport(true)}>
          <View style={[msStyles.reportIcon, { backgroundColor: moduleColor + '18' }]}>
            <MaterialCommunityIcons name={rep.icon} size={20} color={moduleColor} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[msStyles.reportTitle, { color: T.text }]}>{rep.title}</Text>
            <Text style={[msStyles.reportSub, { color: T.sub }]}>{rep.sub}</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={18} color={T.line} />
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderSettingsTab = () => (
    <ScrollView style={{ flex: 1, padding: 16 }}>
      {[
        { section: 'Module', items: [{ label: 'Module Configuration', icon: 'cog', sub: `${module?.name} settings` }, { label: 'Permissions', icon: 'shield-account', sub: 'Role-based access' }, { label: 'Integrations', icon: 'link', sub: 'API & third-party' }] },
        { section: 'Data', items: [{ label: 'Import Data', icon: 'import', sub: 'CSV / Excel import' }, { label: 'Export Data', icon: 'export', sub: 'Download records', onPress: handleExport }, { label: 'Sync Now', icon: 'sync', sub: 'Force data refresh', onPress: onRefresh }] },
        { section: 'Notifications', items: [{ label: 'Alerts', icon: 'bell', sub: 'Configure email/push alerts' }, { label: 'Reminders', icon: 'alarm', sub: 'Scheduled reminders' }] },
      ].map(group => (
        <View key={group.section} style={{ marginBottom: 20 }}>
          <Text style={[msStyles.settingsGroup, { color: T.sub }]}>{group.section}</Text>
          <View style={[msStyles.settingsCard, { backgroundColor: T.surface, borderColor: T.line }]}>
            {group.items.map((item, i) => (
              <TouchableOpacity key={i} style={[msStyles.settingsRow, { borderBottomColor: T.line, borderBottomWidth: i < group.items.length - 1 ? 1 : 0 }]} onPress={item.onPress}>
                <View style={[msStyles.settingsIcon, { backgroundColor: moduleColor + '15' }]}>
                  <MaterialCommunityIcons name={item.icon} size={16} color={moduleColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[msStyles.settingsLabel, { color: T.text }]}>{item.label}</Text>
                  <Text style={[msStyles.settingsSub, { color: T.sub }]}>{item.sub}</Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={16} color={T.line} />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ))}
    </ScrollView>
  );

  const renderContent = () => {
    if (activeTab === 'dashboard') return renderDashboard();
    if (activeTab === 'reports') return renderReportsTab();
    if (activeTab === 'settings') return renderSettingsTab();
    return renderDataTab(); // data, deals, contacts, employees, etc.
  };

  if (!module) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: T.bg, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <MaterialCommunityIcons name="alert-circle" size={64} color={T.danger} />
        <Text style={{ fontSize: 20, fontWeight: '800', color: T.text, marginTop: 16 }}>Module Not Found</Text>
        <TouchableOpacity style={{ marginTop: 24, backgroundColor: T.blue, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 12 }} onPress={() => navigation?.goBack()}>
          <Text style={{ color: '#fff', fontWeight: '700' }}>Go Back</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
      <StatusBar barStyle="light-content" backgroundColor={moduleColor} />

      {/* Header */}
      <LinearGradient colors={[moduleColor, moduleColor + 'cc']} style={msStyles.header}>
        <View style={msStyles.headerRow}>
          <TouchableOpacity style={msStyles.backBtn} onPress={() => navigation?.goBack()}>
            <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
          </TouchableOpacity>
          <View style={[msStyles.headerIcon, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
            <MaterialCommunityIcons name={module.icon} size={22} color="#fff" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={msStyles.headerTitle}>{module.name}</Text>
            <Text style={msStyles.headerSub}>{module.desc}</Text>
          </View>
          <TouchableOpacity style={msStyles.headerActionBtn} onPress={() => setShowAI(true)}>
            <MaterialCommunityIcons name="robot" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={msStyles.headerActionBtn} onPress={() => navigation?.navigate?.('ERPModuleDetail', { moduleId: module.id, screen: 'details', module })}>
            <MaterialCommunityIcons name="dots-vertical" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {/* Tab bar */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8, marginBottom: -6 }}>
          {tabs.map(tab => (
            <TouchableOpacity key={tab.id} style={[msStyles.tab, activeTab === tab.id && msStyles.tabActive]} onPress={() => setActiveTab(tab.id)}>
              <MaterialCommunityIcons name={tab.icon} size={13} color={activeTab === tab.id ? moduleColor : 'rgba(255,255,255,0.8)'} />
              <Text style={[msStyles.tabText, activeTab === tab.id && { color: moduleColor }]}>{tab.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>

      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        {renderContent()}
      </Animated.View>

      {/* ADD MODAL */}
      <Modal visible={showAdd} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAdd(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <LinearGradient colors={[moduleColor, moduleColor + 'cc']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity style={msStyles.modalClose} onPress={() => { setShowAdd(false); setForm({}); }}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={msStyles.modalTitle}>Add New {module.name} Record</Text>
            <View style={{ width: 40 }} />
          </LinearGradient>
          <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
            <ScrollView style={{ flex: 1, padding: 16 }}>
              {formFields.map((field, i) => (
                <View key={i} style={{ marginBottom: 16 }}>
                  <Text style={[msStyles.fieldLabel, { color: T.text }]}>{field.label}{field.required ? ' *' : ''}</Text>
                  <View style={[msStyles.fieldBox, { backgroundColor: T.surface, borderColor: form[field.key] ? moduleColor : T.line }]}>
                    <MaterialCommunityIcons name={field.icon} size={16} color={T.sub} />
                    <TextInput
                      style={[msStyles.fieldInput, { color: T.text }]}
                      value={form[field.key] || ''}
                      onChangeText={v => setForm(prev => ({ ...prev, [field.key]: v }))}
                      placeholder={field.label}
                      placeholderTextColor={T.sub}
                      keyboardType={field.kb || 'default'}
                      multiline={field.multi}
                      numberOfLines={field.multi ? 3 : 1}
                    />
                  </View>
                </View>
              ))}
              <TouchableOpacity style={[msStyles.saveBtn, { backgroundColor: moduleColor, opacity: saving ? 0.7 : 1 }]} onPress={handleSave} disabled={saving}>
                {saving ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="check" size={20} color="#fff" />}
                <Text style={msStyles.saveBtnText}>{saving ? 'Saving...' : 'Save Record'}</Text>
              </TouchableOpacity>
              <View style={{ height: 40 }} />
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* DETAIL MODAL */}
      <Modal visible={showDetail} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetail(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <LinearGradient colors={[moduleColor, moduleColor + 'cc']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity style={msStyles.modalClose} onPress={() => setShowDetail(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={msStyles.modalTitle}>{selectedRecord?.title}</Text>
            <TouchableOpacity onPress={() => selectedRecord && handleDelete(selectedRecord)}>
              <MaterialCommunityIcons name="delete-outline" size={22} color="rgba(255,255,255,0.85)" />
            </TouchableOpacity>
          </LinearGradient>
          {selectedRecord && (
            <ScrollView style={{ flex: 1, padding: 16 }}>
              <View style={[msStyles.detailHeader, { backgroundColor: T.surface, borderColor: T.line }]}>
                <View style={[msStyles.detailIcon, { backgroundColor: moduleColor + '18' }]}>
                  <MaterialCommunityIcons name={selectedRecord.icon} size={30} color={moduleColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[msStyles.detailTitle, { color: T.text }]}>{selectedRecord.title}</Text>
                  <Text style={[msStyles.detailSub, { color: T.sub }]}>{selectedRecord.sub}</Text>
                </View>
                <View style={[msStyles.statusBadge, { backgroundColor: selectedRecord.badgeColor + '20' }]}>
                  <Text style={[msStyles.statusText, { color: selectedRecord.badgeColor }]}>{selectedRecord.badge}</Text>
                </View>
              </View>
              <View style={[msStyles.detailCard, { backgroundColor: T.surface, borderColor: T.line }]}>
                {[
                  { label: 'Value', value: selectedRecord.value },
                  { label: 'Status', value: selectedRecord.status },
                  { label: 'Last Updated', value: selectedRecord.meta },
                  { label: 'Record ID', value: selectedRecord.id },
                  { label: 'Module', value: module.name },
                ].filter(f => f.value).map((field, i) => (
                  <View key={i} style={[msStyles.detailRow, { borderBottomColor: T.line, borderBottomWidth: i < 4 ? 1 : 0 }]}>
                    <Text style={[msStyles.detailLabel, { color: T.sub }]}>{field.label}</Text>
                    <Text style={[msStyles.detailValue, { color: T.text }]}>{field.value}</Text>
                  </View>
                ))}
              </View>
              <View style={msStyles.detailActions}>
                {[
                  { label: 'Edit Record', icon: 'pencil', color: moduleColor, onPress: () => { setShowDetail(false); setForm({ title: selectedRecord.title }); setShowAdd(true); } },
                  { label: 'AI Analysis', icon: 'robot', color: '#6366f1', onPress: () => { setShowDetail(false); setShowAI(true); } },
                  { label: 'Share', icon: 'share', color: T.mint, onPress: () => Share.share({ message: `${selectedRecord.title}\n${selectedRecord.sub}\nValue: ${selectedRecord.value}` }) },
                  { label: 'Delete', icon: 'delete', color: T.danger, onPress: () => handleDelete(selectedRecord) },
                ].map((a, i) => (
                  <TouchableOpacity key={i} style={[msStyles.detailAction, { borderColor: a.color + '40', backgroundColor: a.color + '10' }]} onPress={a.onPress}>
                    <MaterialCommunityIcons name={a.icon} size={18} color={a.color} />
                    <Text style={[msStyles.detailActionText, { color: a.color }]}>{a.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          )}
        </SafeAreaView>
      </Modal>

      {/* REPORT MODAL */}
      <Modal visible={showReport} animationType="fade" transparent onRequestClose={() => setShowReport(false)}>
        <View style={msStyles.overlay}>
          <View style={[msStyles.reportCard, { backgroundColor: T.surface }]}>
            <Text style={[msStyles.reportCardTitle, { color: T.text }]}>{module.name} Report</Text>
            <Text style={[msStyles.reportCardSub, { color: T.sub }]}>Generated {new Date().toLocaleString()}</Text>
            {moduleData.kpis?.map((kpi, i) => (
              <View key={i} style={[msStyles.reportKpiRow, { borderBottomColor: T.line, borderBottomWidth: i < moduleData.kpis.length - 1 ? 1 : 0 }]}>
                <Text style={[msStyles.reportKpiLabel, { color: T.sub }]}>{kpi.l}</Text>
                <Text style={[msStyles.reportKpiValue, { color: kpi.c }]}>{kpi.v}</Text>
                <Text style={[msStyles.reportKpiTrend, { color: T.sub }]}>{kpi.t}</Text>
              </View>
            ))}
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
              <TouchableOpacity style={[msStyles.reportExportBtn, { flex: 1, backgroundColor: moduleColor }]} onPress={handleExport}>
                <MaterialCommunityIcons name="export" size={16} color="#fff" />
                <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>Export</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[msStyles.reportExportBtn, { flex: 1, backgroundColor: T.line }]} onPress={() => setShowReport(false)}>
                <Text style={{ color: T.sub, fontWeight: '700', fontSize: 13 }}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* FILTER MODAL */}
      <Modal visible={showFilter} animationType="fade" transparent onRequestClose={() => setShowFilter(false)}>
        <View style={msStyles.overlay}>
          <View style={[msStyles.filterCard, { backgroundColor: T.surface }]}>
            <Text style={[msStyles.filterTitle, { color: T.text }]}>Filter Records</Text>
            {['all', 'active', 'pending', 'completed', 'overdue', 'in-progress'].map(status => (
              <TouchableOpacity key={status} style={[msStyles.filterOption, filterStatus === status && { backgroundColor: moduleColor + '15', borderColor: moduleColor }, { borderColor: T.line }]} onPress={() => { setFilterStatus(status); setShowFilter(false); }}>
                <View style={[msStyles.filterDot, { backgroundColor: filterStatus === status ? moduleColor : T.line }]} />
                <Text style={[msStyles.filterText, { color: filterStatus === status ? moduleColor : T.text }]}>{status === 'all' ? 'All Records' : status.replace('-', ' ').replace(/^\w/, c => c.toUpperCase())}</Text>
                {filterStatus === status && <MaterialCommunityIcons name="check" size={16} color={moduleColor} />}
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={msStyles.filterClose} onPress={() => setShowFilter(false)}>
              <Text style={{ color: T.sub, fontWeight: '600' }}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* AI MODAL */}
      <Modal visible={showAI} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAI(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <LinearGradient colors={['#6366f1', '#8b5cf6']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity style={msStyles.modalClose} onPress={() => setShowAI(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <View style={{ flex: 1 }}>
              <Text style={msStyles.modalTitle}>{module.name} AI Advisor</Text>
              <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>Claude AI · Module-aware</Text>
            </View>
          </LinearGradient>
          <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
            <ScrollView style={{ flex: 1, padding: 16 }}>
              {aiMessages.map((msg, i) => (
                <View key={i} style={[msStyles.chatRow, msg.role === 'user' && { justifyContent: 'flex-end' }]}>
                  {msg.role === 'assistant' && <View style={msStyles.chatAI}><MaterialCommunityIcons name="robot" size={12} color="#8b5cf6" /></View>}
                  <View style={[msStyles.chatBubble, msg.role === 'user' ? { backgroundColor: '#6366f1' } : { backgroundColor: T.surface, borderColor: T.line, borderWidth: 1 }]}>
                    <Text style={[msStyles.chatText, msg.role === 'user' ? { color: '#fff' } : { color: T.text }]}>{msg.text}</Text>
                  </View>
                </View>
              ))}
              {aiLoading && <View style={msStyles.chatRow}><View style={msStyles.chatAI}><MaterialCommunityIcons name="robot" size={12} color="#8b5cf6" /></View><View style={[msStyles.chatBubble, { backgroundColor: T.surface, borderColor: T.line, borderWidth: 1 }]}><Text style={{ color: T.sub }}>Thinking...</Text></View></View>}
            </ScrollView>
            <View style={[msStyles.chatInputRow, { backgroundColor: T.surface, borderTopColor: T.line }]}>
              <TextInput style={[msStyles.chatInput, { backgroundColor: T.bg, borderColor: T.line, color: T.text }]} value={aiInput} onChangeText={setAiInput} placeholder={`Ask about ${module.name}...`} placeholderTextColor={T.sub} multiline />
              <TouchableOpacity style={[msStyles.chatSend, { backgroundColor: aiInput.trim() ? '#6366f1' : T.line }]} onPress={sendModuleAI} disabled={!aiInput.trim() || aiLoading}>
                <MaterialCommunityIcons name="send" size={16} color={aiInput.trim() ? '#fff' : T.sub} />
              </TouchableOpacity>
            </View>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const { width: W } = Dimensions.get('window');
const msStyles = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerIcon: { width: 38, height: 38, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 1 },
  headerActionBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 18, marginRight: 6, marginBottom: 8 },
  tabActive: { backgroundColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: '600' },
  kpiCard: { width: 125, borderRadius: 16, padding: 13, marginRight: 10, borderWidth: 1, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 },
  kpiIcon: { width: 32, height: 32, borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  kpiValue: { fontSize: 18, fontWeight: '800', marginBottom: 2 },
  kpiLabel: { fontSize: 11, fontWeight: '600', marginBottom: 2 },
  kpiTrend: { fontSize: 10 },
  aiStrip: { marginHorizontal: 16, borderRadius: 14, overflow: 'hidden', marginBottom: 16 },
  aiStripGrad: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 13 },
  aiStripText: { flex: 1, color: '#fff', fontSize: 12, fontWeight: '500' },
  sectionTitle: { fontSize: 15, fontWeight: '700', marginBottom: 10 },
  sectionHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  viewAll: { fontSize: 12, fontWeight: '600' },
  quickActions: { flexDirection: 'row', gap: 10 },
  quickAction: { flex: 1, borderRadius: 14, padding: 12, alignItems: 'center', gap: 8, borderWidth: 1, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3 },
  quickActionIcon: { width: 40, height: 40, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  quickActionLabel: { fontSize: 11, fontWeight: '600', textAlign: 'center' },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderBottomWidth: 1 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 10, borderWidth: 1 },
  searchInput: { flex: 1, fontSize: 13 },
  toolBtn: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center', borderWidth: 1 },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10 },
  addBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },
  recordCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 13, borderRadius: 14, borderWidth: 1, marginBottom: 8, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  recordIcon: { width: 44, height: 44, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  recordTitle: { fontSize: 13, fontWeight: '700', marginBottom: 2 },
  recordSub: { fontSize: 11, marginBottom: 2 },
  recordMeta: { fontSize: 10 },
  recordValue: { fontSize: 13, fontWeight: '700' },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  statusText: { fontSize: 10, fontWeight: '700' },
  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '700', marginTop: 14, marginBottom: 6 },
  emptySub: { fontSize: 13, marginBottom: 20, textAlign: 'center' },
  emptyBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12 },
  emptyBtnText: { color: '#fff', fontSize: 14, fontWeight: '700' },
  pipelineCard: { borderRadius: 16, padding: 14, borderWidth: 1, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6 },
  pipelineRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  pipelineDot: { width: 8, height: 8, borderRadius: 4 },
  pipelineName: { width: 90, fontSize: 12, fontWeight: '600' },
  pipelineBarOuter: { flex: 1, height: 6, borderRadius: 3, backgroundColor: '#e2e8f0', overflow: 'hidden' },
  pipelineBarInner: { height: '100%', borderRadius: 3 },
  pipelineCount: { width: 26, fontSize: 11, textAlign: 'right' },
  pipelineValue: { width: 54, fontSize: 11, fontWeight: '700', textAlign: 'right' },
  deptCard: { width: 150, borderRadius: 14, padding: 14, marginRight: 10, borderWidth: 1, borderLeftWidth: 4, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3 },
  deptName: { fontSize: 13, fontWeight: '700', marginBottom: 6 },
  deptCount: { fontSize: 16, fontWeight: '800', marginBottom: 2 },
  deptBudget: { fontSize: 12, marginBottom: 4 },
  deptLead: { fontSize: 10 },
  accountsCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  accountRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14 },
  accountDot: { width: 8, height: 8, borderRadius: 4 },
  accountName: { flex: 1, fontSize: 13, fontWeight: '600' },
  accountType: { fontSize: 11, textTransform: 'capitalize' },
  accountBalance: { fontSize: 14, fontWeight: '800' },
  whCard: { width: 160, borderRadius: 16, padding: 14, marginRight: 10, borderWidth: 1, gap: 6, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  whName: { fontSize: 13, fontWeight: '700' },
  whLoc: { fontSize: 11 },
  whBarOuter: { height: 6, borderRadius: 3, backgroundColor: '#e2e8f0', overflow: 'hidden' },
  whBarInner: { height: '100%', borderRadius: 3 },
  whPct: { fontSize: 10, fontWeight: '600' },
  kpiListCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  kpiListRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12 },
  kpiStatusDot: { width: 8, height: 8, borderRadius: 4 },
  kpiListName: { flex: 1, fontSize: 13, fontWeight: '600' },
  kpiListTarget: { fontSize: 11, marginRight: 8 },
  kpiListValue: { fontSize: 14, fontWeight: '800', marginRight: 4 },
  taskCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, borderWidth: 1, borderLeftWidth: 3, marginBottom: 8, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  taskTitle: { fontSize: 13, fontWeight: '600', marginBottom: 3 },
  taskMeta: { fontSize: 11 },
  taskDue: { fontSize: 10 },
  reportRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  reportIcon: { width: 44, height: 44, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  reportTitle: { fontSize: 13, fontWeight: '700', marginBottom: 3 },
  reportSub: { fontSize: 11 },
  settingsGroup: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, marginLeft: 4 },
  settingsCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  settingsRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14 },
  settingsIcon: { width: 32, height: 32, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  settingsLabel: { fontSize: 14, fontWeight: '600' },
  settingsSub: { fontSize: 11, marginTop: 1 },
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  modalTitle: { flex: 1, color: '#fff', fontSize: 16, fontWeight: '700' },
  fieldLabel: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  fieldBox: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, borderWidth: 1 },
  fieldInput: { flex: 1, fontSize: 14 },
  saveBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16, borderRadius: 16, marginTop: 8 },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
  detailHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16, borderRadius: 16, borderWidth: 1, marginBottom: 14, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 },
  detailIcon: { width: 56, height: 56, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  detailTitle: { fontSize: 16, fontWeight: '800', marginBottom: 4 },
  detailSub: { fontSize: 12 },
  detailCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginBottom: 16, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', padding: 14 },
  detailLabel: { fontSize: 13 },
  detailValue: { fontSize: 13, fontWeight: '700' },
  detailActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 30 },
  detailAction: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 12, borderWidth: 1, flex: 1, minWidth: (W - 52) / 2, justifyContent: 'center' },
  detailActionText: { fontSize: 13, fontWeight: '700' },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  reportCard: { borderRadius: 24, padding: 24, margin: 16, elevation: 10, shadowColor: '#000', shadowOffset: { width: 0, height: -4 }, shadowOpacity: 0.15, shadowRadius: 12 },
  reportCardTitle: { fontSize: 18, fontWeight: '800', marginBottom: 4 },
  reportCardSub: { fontSize: 12, marginBottom: 16 },
  reportKpiRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10 },
  reportKpiLabel: { flex: 1, fontSize: 13 },
  reportKpiValue: { fontSize: 16, fontWeight: '800', marginRight: 8 },
  reportKpiTrend: { fontSize: 12 },
  reportExportBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, borderRadius: 12 },
  filterCard: { borderRadius: 24, padding: 24, margin: 16, elevation: 10, shadowColor: '#000', shadowOffset: { width: 0, height: -4 }, shadowOpacity: 0.15, shadowRadius: 12 },
  filterTitle: { fontSize: 18, fontWeight: '800', marginBottom: 16 },
  filterOption: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 12, borderWidth: 1, marginBottom: 8 },
  filterDot: { width: 10, height: 10, borderRadius: 5 },
  filterText: { flex: 1, fontSize: 14, fontWeight: '600', textTransform: 'capitalize' },
  filterClose: { alignItems: 'center', padding: 14 },
  chatRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 12 },
  chatAI: { width: 26, height: 26, borderRadius: 13, backgroundColor: '#ede9fe', justifyContent: 'center', alignItems: 'center' },
  chatBubble: { maxWidth: W * 0.72, padding: 12, borderRadius: 16 },
  chatText: { fontSize: 13, lineHeight: 18 },
  chatInputRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderTopWidth: 1 },
  chatInput: { flex: 1, borderWidth: 1, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, fontSize: 13, maxHeight: 80 },
  chatSend: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
});

export default ERPModuleScreen;